import type { CampaignPeriodStatus } from './campaign-status.js';
import { type DonationStatus, hasReceivedNoMoney, type PaymentStatus } from './donation-status.js';
import type { Permission } from './permissions.js';

/**
 * The trash — docs/decisions/0078-a-deleted-donation-is-kept-and-restored.md.
 * Only a donation still waiting may be deleted. A `fulfilled` one has work,
 * cost and evidence booked against it and is cancelled instead; a `cancelled`
 * one is already off the books, its withdrawal is the record, and there is no
 * clutter left for a delete to clear.
 */
export const DELETABLE_DONATION_STATUSES: readonly DonationStatus[] = ['pending', 'queued'];

/**
 * Why a donation may not be deleted, each a stable code the screen says in
 * Turkish. Every one of them names something a deletion would leave behind as
 * a lie — money, a match, a receipt, a page handed to a field team, an animal
 * carried out, evidence, counted cash — so the answer is to cancel instead.
 */
export const DONATION_DELETE_REFUSALS = [
  'DONATION_ALREADY_DELETED',
  'DONATION_DELETE_NOT_ALLOWED',
  'DONATION_DELETE_HAS_PAYMENT',
  'DONATION_DELETE_HAS_RECONCILIATION',
  'DONATION_DELETE_HAS_RECEIPT',
  'DONATION_DELETE_PAYMENT_LINK_OPEN',
  'DONATION_DELETE_SHARES_CARRIED_OUT',
  'DONATION_DELETE_PRINTED',
  'DONATION_DELETE_HAS_EVIDENCE',
  'DONATION_DELETE_BOX_COLLECTION',
] as const;
export type DonationDeleteRefusal = (typeof DONATION_DELETE_REFUSALS)[number];

/**
 * A deletion says why, in words a reader of *Silinen Kayıtlar* can act on:
 * at least ten characters once trimmed, at most five hundred. Required on a
 * single and a bulk deletion alike, by the contract, the service and — for a
 * deletion made after `0119` — the database; docs/decisions/0085 supersedes
 * 0078's optional reason.
 */
export const DONATION_DELETION_REASON_MIN_LENGTH = 10;
export const DONATION_DELETION_REASON_MAX_LENGTH = 500;

/**
 * The trimmed reason a deletion records, or null when it is missing, blank,
 * shorter than the minimum or longer than the maximum — the one rule the
 * contract, the service and the dialog each ask. Characters are counted as
 * Postgres's `char_length` counts them, by code point, so a reason the
 * database's check would refuse is never accepted here.
 */
export function acceptedDeletionReason(reason: string | null | undefined): string | null {
  const trimmed = reason?.trim() ?? '';
  const length = [...trimmed].length;
  if (length < DONATION_DELETION_REASON_MIN_LENGTH) return null;
  if (length > DONATION_DELETION_REASON_MAX_LENGTH) return null;
  return trimmed;
}

/** What a deletion is judged on, read under the donation's row lock by the writer. */
export type DonationDeletionFacts = {
  status: DonationStatus;
  paymentStatus: PaymentStatus;
  deleted: boolean;
  /** `printed_at`: its page — with the QR the evidence is matched by — is on paper. */
  printed: boolean;
  /** Shares recorded in a carried-out animal (`fulfilled_queue_group_shares`). */
  recordedShares: number;
  /** Rows of `reconciliations` naming it, in any status. */
  reconciliations: number;
  /** Rows of `refunds` naming it. */
  refunds: number;
  /** Rows of `receipts` naming it, issued or voided. */
  receipts: number;
  /** `payment_links` naming it that are `active` or `paid`. */
  openPaymentLinks: number;
  /** Evidence attached to it, or an inbound medium matched to it. */
  evidence: number;
  /** A box emptying's donation: the record of counted cash. */
  fromBoxCollection: boolean;
};

/**
 * The first reason this donation may not be deleted, or null. Asked by the
 * service for the offer and by the writer under the row's `FOR UPDATE` lock,
 * where it decides.
 */
export function donationDeletionRefusal(
  facts: DonationDeletionFacts,
): DonationDeleteRefusal | null {
  if (facts.deleted) return 'DONATION_ALREADY_DELETED';
  if (!DELETABLE_DONATION_STATUSES.includes(facts.status)) return 'DONATION_DELETE_NOT_ALLOWED';
  if (!hasReceivedNoMoney(facts.paymentStatus) || facts.refunds > 0) {
    return 'DONATION_DELETE_HAS_PAYMENT';
  }
  if (facts.reconciliations > 0) return 'DONATION_DELETE_HAS_RECONCILIATION';
  if (facts.receipts > 0) return 'DONATION_DELETE_HAS_RECEIPT';
  if (facts.openPaymentLinks > 0) return 'DONATION_DELETE_PAYMENT_LINK_OPEN';
  if (facts.recordedShares > 0) return 'DONATION_DELETE_SHARES_CARRIED_OUT';
  if (facts.printed) return 'DONATION_DELETE_PRINTED';
  if (facts.evidence > 0) return 'DONATION_DELETE_HAS_EVIDENCE';
  if (facts.fromBoxCollection) return 'DONATION_DELETE_BOX_COLLECTION';
  return null;
}

/**
 * Why a deleted donation may not be put back. The world it was deleted from
 * may have moved on: a closed season's books are closed and its totals final,
 * and a yurt whose assignment was withdrawn can no longer carry it out.
 */
export const DONATION_RESTORE_REFUSALS = [
  'DONATION_NOT_DELETED',
  'DONATION_RESTORE_PERIOD_CLOSED',
  'DONATION_RESTORE_ASSIGNMENT_INACTIVE',
] as const;
export type DonationRestoreRefusal = (typeof DONATION_RESTORE_REFUSALS)[number];

export type DonationRestoreFacts = {
  deleted: boolean;
  periodStatus: CampaignPeriodStatus;
  /** Null when the donation names no yurt; otherwise whether its assignment is still active. */
  yurtAssignmentActive: boolean | null;
};

export function donationRestoreRefusal(facts: DonationRestoreFacts): DonationRestoreRefusal | null {
  if (!facts.deleted) return 'DONATION_NOT_DELETED';
  if (facts.periodStatus === 'closed') return 'DONATION_RESTORE_PERIOD_CLOSED';
  if (facts.yurtAssignmentActive === false) return 'DONATION_RESTORE_ASSIGNMENT_INACTIVE';
  return null;
}

/**
 * The permission a deletion of this row needs beyond `donation.delete`, or
 * null. A `queued` donation has left the entering clerk's hands, and deleting
 * it is at least as much as correcting it, so it needs `donation.editQueued`
 * as a correction does — docs/decisions/0071.
 */
export function deletionNeedsPermission(
  row: { readonly status: DonationStatus },
  editor: { readonly permissions: readonly Permission[] },
): Permission | null {
  if (!editor.permissions.includes('donation.delete')) return 'donation.delete';
  if (row.status === 'queued' && !editor.permissions.includes('donation.editQueued')) {
    return 'donation.editQueued';
  }
  return null;
}
